import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Eye, X } from "lucide-react";

// Hide-and-Pounce mini-game. Wait for the strike window, then tap "Pounce!".
// Tap too early => startled. Miss the window => ambushed.
export default function RugTrap({ open, onClose, onResult }) {
  const [phase, setPhase] = useState("stalking"); // stalking | ready | done
  const [resultText, setResultText] = useState("");
  const delayRef = useRef(null);
  const windowRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPhase("stalking");
    setResultText("");
    const delay = 1400 + Math.random() * 2400; // unpredictable tell
    delayRef.current = setTimeout(() => {
      setPhase("ready");
      windowRef.current = setTimeout(() => {
        // player missed the window => ambushed
        finish("ambushed");
      }, 1000);
    }, delay);
    return () => {
      clearTimeout(delayRef.current);
      clearTimeout(windowRef.current);
      clearTimeout(closeRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const finish = (kind) => {
    clearTimeout(delayRef.current);
    clearTimeout(windowRef.current);
    setPhase("done");
    if (kind === "survived") setResultText("POUNCE SURVIVED!");
    else if (kind === "early") setResultText("TOO SOON — STARTLED!");
    else setResultText("AMBUSHED! She got you.");
    onResult(kind);
    closeRef.current = setTimeout(() => onClose(), 1100);
  };

  const handleTap = () => {
    if (phase === "stalking") finish("early");
    else if (phase === "ready") finish("survived");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="rug-trap-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="glass relative w-full max-w-lg rounded-3xl p-6"
      >
        <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-white" data-testid="rug-close-button" style={{ transition: "color 0.2s ease" }}>
          <X className="h-5 w-5" />
        </button>

        <h3 className="font-display text-xl font-extrabold text-pink-300">The Rug Trap</h3>
        <p className="font-mono-ui text-[11px] uppercase tracking-widest text-slate-400">Wait for her eyes to flare, then pounce back.</p>

        {/* rug arena */}
        <div
          onClick={handleTap}
          data-testid="rug-arena"
          className="rug-weave relative mt-5 flex h-56 cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-slate-700"
        >
          {/* glowing eyes */}
          <div className="flex gap-6">
            {[0, 1].map((i) => (
              <motion.div
                key={i}
                className="rounded-full"
                animate={
                  phase === "ready"
                    ? { backgroundColor: "#38BDF8", boxShadow: "0 0 30px 10px rgba(56,189,248,0.9)", scale: 1.3 }
                    : { backgroundColor: "#0EA5E9", boxShadow: "0 0 10px 2px rgba(56,189,248,0.35)", scale: 1 }
                }
                transition={{ duration: 0.2 }}
                style={{ width: 26, height: 16, borderRadius: "50%" }}
              />
            ))}
          </div>

          {/* twitching tail */}
          <div className="tail-twitch absolute bottom-3 right-6 h-2 w-24 rounded-full bg-slate-500/70" />

          {/* status text */}
          <div className="pointer-events-none absolute inset-x-0 bottom-3 text-center">
            {phase === "stalking" && <span className="font-mono-ui text-xs text-slate-400">stalking… hold your nerve</span>}
            {phase === "ready" && (
              <motion.span
                initial={{ scale: 0.8 }}
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ repeat: Infinity, duration: 0.4 }}
                className="font-display text-2xl font-black text-sky-300"
              >
                NOW! POUNCE!
              </motion.span>
            )}
            {phase === "done" && <span className="font-display text-xl font-black text-amber-300">{resultText}</span>}
          </div>
        </div>

        <button
          onClick={handleTap}
          disabled={phase === "done"}
          data-testid="pounce-button"
          style={{ transition: "transform 0.15s ease, background-color 0.2s ease" }}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-pink-500 py-3 font-display text-lg font-bold text-white hover:bg-pink-400 hover:scale-[1.02] active:scale-95 disabled:opacity-40"
        >
          <Eye className="h-5 w-5" /> Pounce!
        </button>
      </motion.div>
    </div>
  );
}
